(ns renolit-roofing-service.view.laboratory-sample-analysis
  (:require [dev.gethop.session.re-frame.cognito.token :as cognito.token]
            [malli.util :as mu]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [renolit-roofing-service.layout.components :refer [collapsable-section
                                                               entity-section]]
            [renolit-roofing-service.layout.debouncer :as layout.debouncer]
            [renolit-roofing-service.layout.display-item :as layout.display-item]
            [renolit-roofing-service.layout.display-item.impl]
            [renolit-roofing-service.layout.form :as layout.form]
            [renolit-roofing-service.layout.icon :as layout.icon]
            [renolit-roofing-service.localization :refer [t]]
            [renolit-roofing-service.navigation :as nav]
            [renolit-roofing-service.service.service :as srv.service]
            [renolit-roofing-service.util :as util]
            [renolit-roofing-service.shared.util.time :as shared.time]
            [renolit-roofing-service.view :as view]))

(def path-params-schema
  [:map
   [:id
    [:uuid]]])

(def ^:const route-config
  {:name [:laboratory-sample-analysis]
   :auth-req? true
   :parameters {:path path-params-schema}
   :controllers [{:parameters {:path (mu/keys path-params-schema)}
                  :start #(rf/dispatch [::view.on-enter %])
                  :stop #(rf/dispatch [::view.on-leave %])}]})

(rf/reg-event-fx
 ::view.on-enter
 (fn [_ [_ opts]]
   (let [service-id (get-in opts [:path :id])]
     {:fx [[:dispatch [::srv.service/fetch-service-by-id service-id
                       {:on-success-evt [::service-loaded]}]]
           [:dispatch [::set-current-sample-index 0]]]})))

(rf/reg-event-fx
 ::service-loaded
 (fn [{:keys [db]} [_ service]]
   (let [analyses (or (:analyses service) [])
         analyses-map (reduce (fn [acc analysis]
                                (assoc acc (:observation-id analysis)
                                       (dissoc analysis :observation-id)))
                              {}
                              analyses)]
     {:db (assoc db :lab/sample-analyses analyses-map)
      :fx [[:dispatch [::load-current-sample-image]]]})))

(rf/reg-event-fx
 ::view.on-leave
 (fn [{:keys [db]} _]
   {:db (dissoc db
                :lab/current-sample-index
                :lab/sample-analyses
                :lab/show-success-modal?
                :lab/presigned-urls)
    :fx [[:dispatch [::srv.service/clear-service]]]}))

(rf/reg-event-db
 ::set-current-sample-index
 (fn [db [_ idx]]
   (assoc db :lab/current-sample-index idx)))

(rf/reg-event-fx
 ::navigate-to-sample
 (fn [{:keys [db]} [_ new-index]]
   {:db (assoc db :lab/current-sample-index new-index)
    :fx [[:dispatch [::load-current-sample-image]]]}))

(defn- enrich-analysis
  "Ensure the analysis map has all required fields before sending to the backend."
  [analysis sample]
  (let [first-image (first (:images sample))
        object-id (:object-id first-image)]
    (cond-> (merge {:valid false :request-repetition false} analysis)
      (not (:id analysis))
      (assoc :id (random-uuid))
      (and object-id (not (:image-id analysis)))
      (assoc :image-id object-id)
      (and object-id (not (:object-id analysis)))
      (assoc :object-id object-id))))

(def ^:private debounced-auto-save
  (layout.debouncer/debouncer
   (fn [observation-id]
     (rf/dispatch [::auto-save-analysis observation-id]))
   1000))

(rf/reg-fx
 ::debounced-save
 (fn [observation-id]
   (debounced-auto-save observation-id)))

(rf/reg-event-fx
 ::update-analysis-field
 (fn [{:keys [db]} [_ observation-id field value]]
   {:db (assoc-in db [:lab/sample-analyses observation-id field] value)
    ::debounced-save observation-id}))

(rf/reg-event-fx
 ::auto-save-analysis
 (fn [{:keys [db]} [_ observation-id]]
   (let [service (get db :current-service)
         service-id (:id service)
         observations (or (:observations service) [])
         sample (first (filter #(= (:id %) observation-id) observations))
         analysis (get-in db [:lab/sample-analyses observation-id])
         enriched (enrich-analysis analysis sample)]
     (if (and service-id (seq analysis))
       {:db (assoc-in db [:lab/sample-analyses observation-id] enriched)
        :fx [[:dispatch [::srv.service/upsert-service-analysis
                         service-id observation-id enriched
                         {:on-success-evt [::auto-save-success]
                          :on-failure-evt [::auto-save-failure]}]]]}
       {}))))

(rf/reg-event-db
 ::auto-save-success
 (fn [db _] db))

(rf/reg-event-db
 ::auto-save-failure
 (fn [db _] db))

(rf/reg-event-fx
 ::finalize-report
 (fn [{:keys [db]} _]
   {:db (assoc db :lab/show-success-modal? true)}))

(rf/reg-event-fx
 ::dismiss-success-modal
 (fn [{:keys [db]} _]
   (let [service-id (get-in db [:current-service :id])]
     {:db (-> db
              (assoc :lab/show-success-modal? false)
              (dissoc :lab/sample-analyses))
      :fx [[:dispatch [::nav/push-state [:laboratory-report] {:id service-id}]]]})))

(rf/reg-event-fx
 ::load-current-sample-image
 (fn [{:keys [db]} _]
   (let [service (get db :current-service)
         observations (or (:observations service) [])
         samples (->> observations
                      (filter #(not (:clear %)))
                      vec)
         idx (get db :lab/current-sample-index 0)
         sample (get samples idx)
         images (or (:images sample) [])
         first-image (first images)
         object-id (:object-id first-image)]
     (if (and object-id
              (not (get-in db [:lab/presigned-urls object-id])))
       {:fx [[:dispatch [::fetch-read-presigned-url (:id service) object-id]]]}
       {}))))

;; TODO: add it to service file
(rf/reg-event-fx
 ::fetch-read-presigned-url
 [(rf/inject-cofx ::cognito.token/session)]
 (fn [{:keys [session]} [_ service-id object-id]]
   (when session
     {:http-xhrio
      {:headers {"Authorization" (str "Bearer " (get-in session [:id-token :jwt]))}
       :method :post
       :uri (str "/api/services/" service-id "/images")
       :params {:object-id object-id
                :content-type "image/jpeg"
                :operation :read}
       :format (util/ajax-transit-request-format)
       :response-format (util/ajax-transit-response-format)
       :on-success [::presigned-url-received object-id]
       :on-failure [::util/generic-error]}})))

(rf/reg-event-db
 ::presigned-url-received
 (fn [db [_ object-id {:keys [object-url]}]]
   (assoc-in db [:lab/presigned-urls object-id] object-url)))

(rf/reg-sub
 ::lab-samples
 :<- [::srv.service/current-service]
 (fn [service _]
   (let [observations (or (:observations service) [])
         zones (or (:zones service) [])
         reviews (or (:reviews service) [])
         zone-index (into {} (map (juxt :id :name)) zones)
         review-index (into {} (map (juxt :id identity)) reviews)]
     (->> observations
          (filter #(not (:clear %)))
          (mapv (fn [obs]
                  (let [review (get review-index (:review-id obs))
                        zone-id (:zone-id review)]
                    (assoc obs
                           :zone-name (get zone-index zone-id)
                           :wp-type (:wp-type review)))))))))

(rf/reg-sub
 ::current-sample-index
 (fn [db _]
   (get db :lab/current-sample-index 0)))

(rf/reg-sub
 ::current-sample
 :<- [::lab-samples]
 :<- [::current-sample-index]
 (fn [[samples idx] _]
   (get samples (or idx 0))))

(rf/reg-sub
 ::sample-analyses
 (fn [db _]
   (get db :lab/sample-analyses {})))

(rf/reg-sub
 ::total-samples
 :<- [::lab-samples]
 (fn [samples _]
   (count samples)))

(rf/reg-sub
 ::show-success-modal?
 (fn [db _]
   (get db :lab/show-success-modal? false)))

(rf/reg-sub
 ::presigned-urls
 (fn [db _]
   (get db :lab/presigned-urls {})))

(rf/reg-sub
 ::project
 :<- [::srv.service/current-service]
 (fn [service _]
   (:project service)))

(defn- format-date
  [date]
  (shared.time/format-date date {}))

(defn- build-photo-id
  "Return the photo-id from the first image of the sample."
  [{:keys [images]}]
  (or (:photo-id (first images)) ""))

(defn- sample-photo
  [sample]
  (let [presigned-urls @(rf/subscribe [::presigned-urls])
        images (or (:images sample) [])
        first-image (first images)
        object-id (:object-id first-image)
        url (get presigned-urls object-id)]
    [:div.ls-photo
     (if url
       [:img.ls-photo__img {:src url :alt "Sample photo"}]
       [:div.ls-photo__placeholder
        [layout.icon/icon {:key :camera :class "ls-photo__placeholder-icon"}]])]))

(defn- sample-analysis-form
  [sample analysis-state]
  (let [observation-id (:id sample)
        photo-id (build-photo-id sample)]
    [:div.ls-form
     [:div.ls-form__photo-id
      [:span.ls-form__photo-id-label "Photo ID: "]
      [:span.ls-form__photo-id-value photo-id]]

     [layout.form/item
      {:context :lab-sample-analysis
       :type :radio-button-group
       :state analysis-state
       :path [:valid]
       :label (t [:page.laboratory.sample-analysis/photo-valid])
       :choices [{:id true :name (t [:page.laboratory.sample-analysis/yes])}
                 {:id false :name (t [:page.laboratory.sample-analysis/no])}]
       :on-change {:fn (fn [value]
                         (rf/dispatch [::update-analysis-field observation-id :valid value]))}}]

     [layout.form/item
      {:context :lab-sample-analysis
       :type :input
       :state analysis-state
       :path [:crack-level]
       :schema [:int {:min 1 :max 9}]
       :label (t [:page.laboratory.sample-analysis/crack-level])
       :input-type "number"
       :input-opts {:min 1 :max 9}
       :on-change {:fn (fn [value]
                         (rf/dispatch [::update-analysis-field observation-id :crack-level value]))}}]

     [layout.form/item
      {:context :lab-sample-analysis
       :type :text-area
       :state analysis-state
       :path [:observations]
       :label (t [:page.laboratory.sample-analysis/observations])
       :on-change {:fn (fn [value]
                         (rf/dispatch [::update-analysis-field observation-id :observations value]))}}]

     [layout.form/item
      {:context :lab-sample-analysis
       :type :checkbox
       :state analysis-state
       :path [:request-repetition]
       :label (t [:page.laboratory.sample-analysis/request-repetition])
       :on-change {:fn (fn [value]
                         (rf/dispatch [::update-analysis-field observation-id :request-repetition value]))}}]]))

(defn- sample-info-table
  [sample]
  (let [zone-name (or (:zone-name sample) "-")
        dirt-type (or (:dirt-type sample) "-")
        photo-position (or (:photo-position sample) "-")
        face (or (:face sample) "-")
        wp-type (or (when (:wp-type sample)
                      (t [(keyword (str "domain.choices.wp-type/" (name (:wp-type sample))))]))
                    "-")]
    [:div.ls-info-table
     [:div.ls-info-table__header
      [:span (t [:page.laboratory.sample-analysis.sample-info-headers/field-report])]
      [:span (t [:page.laboratory.sample-analysis.sample-info-headers/zone])]
      [:span (t [:page.laboratory.sample-analysis.sample-info-headers/dirt-type])]
      [:span (t [:page.laboratory.sample-analysis.sample-info-headers/photo-position])]
      [:span (t [:page.laboratory.sample-analysis.sample-info-headers/face])]]
     [:div.ls-info-table__row
      [:span wp-type]
      [:span zone-name]
      [:span dirt-type]
      [:span photo-position]
      [:span face]]]))

(defn- sample-pagination
  [current-index total]
  (let [max-visible 7]
    [:div.ls-pagination
     ;; Previous arrow
     [:button.ls-pagination__arrow
      {:disabled (= current-index 0)
       :on-click #(when (pos? current-index)
                    (rf/dispatch [::navigate-to-sample (dec current-index)]))}
      [layout.icon/icon {:key :back}]]
     [:div.ls-pagination__pages
      (let [start (max 0 (- current-index (quot max-visible 2)))
            end (min total (+ start max-visible))
            adjusted-start (max 0 (- end max-visible))
            visible-pages (range adjusted-start end)]
        [:<>
         (when (pos? adjusted-start)
           [:<>
            ^{:key "first-page"}
            [:button.ls-pagination__page
             {:on-click #(rf/dispatch [::navigate-to-sample 0])}
             "1"]
            (when (> adjusted-start 1)
              [:span.ls-pagination__ellipsis "..."])])
         (doall
          (for [page-idx visible-pages]
            ^{:key page-idx}
            [:button.ls-pagination__page
             {:class (when (= page-idx current-index) "ls-pagination__page--active")
              :on-click #(rf/dispatch [::navigate-to-sample page-idx])}
             (str (inc page-idx))]))
         (when (< end total)
           [:<>
            (when (< end (dec total))
              [:span.ls-pagination__ellipsis "..."])
            ^{:key "last-page"}
            [:button.ls-pagination__page
             {:on-click #(rf/dispatch [::navigate-to-sample (dec total)])}
             (str total)]])])]
     [:button.ls-pagination__arrow
      {:disabled (= current-index (dec total))
       :on-click #(when (< current-index (dec total))
                    (rf/dispatch [::navigate-to-sample (inc current-index)]))}
      [layout.icon/icon {:key :right-arrow}]]]))

(defn- sample-card*
  "Form-2 component: outer fn initializes local state atom,
   inner fn renders and re-syncs atom when the sample changes."
  [_initial-sample _initial-analysis]
  (let [analysis-state (r/atom {})
        prev-obs-id (r/atom nil)]
    (fn [sample current-analysis]
      (let [observation-id (:id sample)
            current-index @(rf/subscribe [::current-sample-index])
            total @(rf/subscribe [::total-samples])]
        ;; Sync local atom when navigating to a different sample
        (when (not= observation-id @prev-obs-id)
          (reset! prev-obs-id observation-id)
          (reset! analysis-state (merge {:valid false} current-analysis)))
        (when sample
          [:div.ls-card
           [:div.ls-card__header
            [:div.ls-card__title
             [layout.icon/icon {:key :flask :class "ls-card__icon"}]
             [:span (str (t [:page.laboratory.sample-analysis/sample]) " " (inc current-index))]]
            (when (< current-index (dec total))
              [:button.btn.btn--secondary.btn--small
               {:on-click #(rf/dispatch [::navigate-to-sample (inc current-index)])}
               (t [:page.laboratory.sample-analysis/next])])]
           [:div.ls-card__content
            [sample-photo sample]
            [sample-analysis-form sample analysis-state]]
           [sample-info-table sample]
           (when (> total 1)
             [sample-pagination current-index total])])))))

(defn- sample-card
  []
  (let [sample @(rf/subscribe [::current-sample])
        analyses @(rf/subscribe [::sample-analyses])
        current-analysis (get analyses (:id sample) {})]
    (when sample
      [sample-card* sample current-analysis])))

(defn- project-info-section
  [project]
  [collapsable-section
   {:title (t [:page.laboratory.sample-analysis/project-info])}
   [:div.ls-project-info
    [layout.display-item/main
     {:type :text
      :state project
      :path [:name]
      :label-transl-kw :domain.project/project-name}]
    [layout.display-item/main
     {:type :text
      :state project
      :path [:number]
      :label-transl-kw :domain.project/project-number}]
    [layout.display-item/main
     {:type :text
      :state project
      :path [:account-name]
      :label-transl-kw :domain.project/account-name}]
    [layout.display-item/main
     {:type :text
      :state project
      :path [:building-type]
      :label-transl-kw :domain.project/building-type}]
    [layout.display-item/main
     {:type :text
      :state project
      :path [:product]
      :label-transl-kw :domain.project/product}]
    [layout.display-item/main
     {:type :text
      :state project
      :path [:end-customer-name]
      :label-transl-kw :domain.project/end-customer-name}]]])

(defn- service-info-section
  [service]
  [collapsable-section
   {:title (t [:page.laboratory.sample-analysis/service-info])}
   [:div.ls-service-info
    [layout.display-item/main
     {:type :text
      :state service
      :path [:name]
      :label-transl-kw :domain.service/name}]
    [layout.display-item/main
     {:type :text
      :state service
      :path [:origin]
      :label-transl-kw :domain.service/origin}]
    [layout.display-item/main
     {:type :text
      :state service
      :path [:reason-text]
      :label-transl-kw :domain.service/reason-text}]
    [layout.display-item/main
     {:type :text
      :state service
      :path [:objective]
      :label-transl-kw :domain.service/objective}]
    [layout.display-item/main
     {:type :text
      :state service
      :path [:manager]
      :label-transl-kw :domain.service/responsible}]
    [layout.display-item/main
     {:type :text
      :state service
      :path [:inspector]
      :label-transl-kw :domain.service/inspector}]
    [layout.display-item/main
     {:type :text
      :value (format-date (:last-diagnosis-date @service))
      :label-transl-kw :domain.service/last-diagnosis-date}]]])

(defn- success-modal
  []
  [:div.ls-modal__overlay
   [:div.ls-modal__dialog
    [layout.icon/icon {:key :success :class "ls-modal__icon"}]
    [:h2.ls-modal__title
     (t [:page.laboratory.sample-analysis/success-title])]
    [:p.ls-modal__body
     (t [:page.laboratory.sample-analysis/success-body])]
    [:button.btn.btn--primary
     {:on-click #(rf/dispatch [::dismiss-success-modal])}
     (t [:page.laboratory.sample-analysis/success-ok])]]])

(defn- main
  []
  (let [service (rf/subscribe [::srv.service/current-service])
        project (rf/subscribe [::project])
        show-modal? (rf/subscribe [::show-success-modal?])]
    (fn []
      (let [title (if @project
                    (str (:name @project)
                         (when (:building-city @project)
                           (str " - " (:building-city @project))))
                    (t [:page.laboratory.sample-analysis/title]))]
        [:div.user_layout
         (when @show-modal?
           [success-modal])
         [:div.user_layout_header
          [:a.back-link {:href (nav/href [:laboratory-home])}
           [layout.icon/icon {:key :back}]
           [:span (t [:page.laboratory.sample-analysis/back-to-list])]]
          [:div]]
         [:div.ls-sample
          (if @service
            [:<>
             [:div.ls-sample__title-bar
              [:h2.ls-sample__page-title title]
              [:button.btn.btn--secondary
               {:on-click #(do
                             (rf/dispatch [::srv.service/mark-service-status-as-finalization-phase (:id @service)])
                             (rf/dispatch [::finalize-report]))}
               (t [:page.laboratory.sample-analysis/finalize-report])]]
             [entity-section
              {:title (t [:page.laboratory.sample-analysis/title])}
              [sample-card]]
             (when @project
               [project-info-section project])
             [service-info-section service]]
            [:div.loading (t [:common.loading])])]]))))

(defmethod view/view-display (:name route-config)
  [_]
  [main])
